import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Form, Input, Select, TextArea } from '@/components';

import { TopcoProjectTechnology } from '@/features/projects/types';

import { nullableStringSetter } from '@/helpers';
import { useCountries } from '@/hooks';

export type TopcoSubmitFields = {
  company_type: string | null;
  other_details: string | null;
  hq_location: string | null;
};

export type TopcoFormFields = {
  company_type: string | null;
  other_details: string | null;
  hq_location: string | null;
};

export type TopcoFormProps = {
  readonly id: string;
  readonly item: TopcoProjectTechnology;
  readonly onSubmit: (data: TopcoSubmitFields) => void;
};

export function TopcoFormNew({ id, item, onSubmit }: TopcoFormProps) {
  const { t } = useTranslation(['projects']);
  const { options: countriesOptions } = useCountries();

  const options = useMemo(
    () => ({
      defaultValues: {
        company_type: item.company_type,
        other_details: item.other_details,
        hq_location: item.hq_location,
      },
    }),
    [item]
  );

  const onSubmitTopcoForm = useCallback(
    (data: TopcoFormFields) => {
      const payload = {
        company_type: data.company_type,
        other_details: data.other_details,
        hq_location: data.hq_location,
      };

      onSubmit(payload);
    },
    [onSubmit]
  );

  return (
    <Form<TopcoFormFields> id={id} onSubmit={(data) => onSubmitTopcoForm(data)} options={options}>
      {({ formState, register }) => (
        <div className="space-y-6">
          <Field
            id="hq_location"
            label={t('HQ Location')}
            error={formState.errors['hq_location']}
            classes={{ field: 'space-y-1' }}
          >
            <Select
              id="hq_location"
              options={countriesOptions}
              placeholder={t('Select a country')}
              color={formState.errors['hq_location'] ? 'error' : 'primary'}
              registration={register('hq_location')}
            />
          </Field>

          <Field
            id="company_type"
            label={t('Company Type')}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="company_type"
              type="text"
              placeholder={t('e.g. Feed in-Tariff')}
              color={formState.errors['company_type'] ? 'error' : 'primary'}
              registration={register('company_type')}
            />
          </Field>

          <Field
            id="other_details"
            label={t('Other Details')}
            error={formState.errors['other_details']}
            delay={0.6}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="other_details"
              color={formState.errors['other_details'] ? 'error' : 'primary'}
              registration={register('other_details', { setValueAs: nullableStringSetter })}
              className="min-h-32"
            />
          </Field>
        </div>
      )}
    </Form>
  );
}
